import { useEffect, useRef, useState, useCallback } from 'react'
import type { Map as MapLibreMap } from 'maplibre-gl'
import { TerraDraw, TerraDrawPolygonMode, TerraDrawSelectMode } from 'terra-draw'
import { TerraDrawMapLibreGLAdapter } from 'terra-draw-maplibre-gl-adapter'

// =====================================================================
// WKT CONVERSION
// =====================================================================

function polygonToWkt(coords: number[][]): string {
  const ring = coords.map(c => `${c[0]} ${c[1]}`).join(', ')
  return `POLYGON((${ring}))`
}

// =====================================================================
// HOOK
// =====================================================================

export interface UseAnalyticsPolygonReturn {
  isDrawing: boolean
  polygonWkt: string | null
  startDrawing: () => void
  clearPolygon: () => void
  attachToMap: (map: MapLibreMap) => void
}

export function useAnalyticsPolygon(): UseAnalyticsPolygonReturn {
  const drawRef = useRef<TerraDraw | null>(null)
  const mapRef = useRef<MapLibreMap | null>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [polygonWkt, setPolygonWkt] = useState<string | null>(null)
  const [mapReady, setMapReady] = useState(false)

  const attachToMap = useCallback((map: MapLibreMap) => {
    // Clean up previous instance
    if (drawRef.current) {
      try { drawRef.current.stop() } catch { /* ignore */ }
      drawRef.current = null
    }
    mapRef.current = map
    setMapReady(true)
  }, [])

  // Initialize terra-draw when map is ready
  useEffect(() => {
    if (!mapReady || !mapRef.current || drawRef.current) return

    const map = mapRef.current
    const draw = new TerraDraw({
      adapter: new TerraDrawMapLibreGLAdapter({ map }),
      modes: [
        new TerraDrawPolygonMode({
          styles: {
            fillColor: '#F59E0B',
            fillOpacity: 0.15,
            outlineColor: '#F59E0B',
            outlineWidth: 2,
            closingPointColor: '#F59E0B',
            closingPointOutlineColor: '#ffffff',
            closingPointWidth: 6,
          },
        }),
        new TerraDrawSelectMode({
          flags: {
            polygon: {
              feature: {
                draggable: true,
                coordinates: {
                  midpoints: true,
                  draggable: true,
                  deletable: true,
                },
              },
            },
          },
        }),
      ],
    })

    draw.start()
    draw.setMode('static')

    // Listen for polygon completion
    draw.on('finish', (id: string | number) => {
      const snapshot = draw.getSnapshot()
      const feature = snapshot.find(f => f.id === id)
      if (feature && feature.geometry.type === 'Polygon') {
        const coords = feature.geometry.coordinates[0] as number[][]
        setPolygonWkt(polygonToWkt(coords))
        // Switch to select mode so user can adjust
        draw.setMode('select')
      }
    })

    // Listen for changes (drag vertices)
    draw.on('change', () => {
      const snapshot = draw.getSnapshot()
      const polygons = snapshot.filter(
        f => f.geometry.type === 'Polygon' && f.properties?.mode === 'polygon'
      )
      if (polygons.length > 0) {
        const coords = (polygons[0].geometry as GeoJSON.Polygon).coordinates[0] as number[][]
        setPolygonWkt(polygonToWkt(coords))
      }
    })

    drawRef.current = draw

    return () => {
      try { draw.stop() } catch { /* ignore */ }
      drawRef.current = null
    }
  }, [mapReady])

  const startDrawing = useCallback(() => {
    const draw = drawRef.current
    if (!draw) return

    // Clear existing polygons first
    const snapshot = draw.getSnapshot()
    const polygonFeatures = snapshot.filter(
      f => f.geometry.type === 'Polygon'
    )
    for (const f of polygonFeatures) {
      try { draw.removeFeatures([f.id as string]) } catch { /* ignore */ }
    }

    setPolygonWkt(null)
    draw.setMode('polygon')
    setIsDrawing(true)
  }, [])

  const clearPolygon = useCallback(() => {
    const draw = drawRef.current
    if (!draw) return

    const snapshot = draw.getSnapshot()
    const polygonFeatures = snapshot.filter(
      f => f.geometry.type === 'Polygon'
    )
    for (const f of polygonFeatures) {
      try { draw.removeFeatures([f.id as string]) } catch { /* ignore */ }
    }

    draw.setMode('static')
    setPolygonWkt(null)
    setIsDrawing(false)
  }, [])

  return {
    isDrawing,
    polygonWkt,
    startDrawing,
    clearPolygon,
    attachToMap,
  }
}
